import { useEffect, useState } from "react";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import type { ToastMessage } from "primereact/toast";
import { getDirectoryClient } from "sj-ui-toolkit/src/client";
import type { User } from "sj-ui-toolkit/src/client/svcDirectory";
import { mapIsAdmin } from "sj-ui-toolkit/src/utils/user";

import { updateUserNotificationsAsync } from "@/api/users";
import type { UserData } from "@/pages/Users";

// ---------------------------------------------------------------------------
// EditUser dialog
// ---------------------------------------------------------------------------

interface EditUserState {
  directoryUuid: string;
  email: string;
  name: { familyName: string; givenName: string };
  attributes: {
    employeeNumber: string;
    jobRole: string;
    title: string;
    country: string;
    company: string;
    businessUnit: string;
    department: string;
    teamName: string;
    managersEmail: string;
    managersName: string;
    securityChampion: boolean;
  };
  admin: boolean;
  receiveActivityNotifications: boolean;
}

export interface EditUserDialogProps {
  visible: boolean;
  user: UserData;
  scimEnabled: boolean;
  notificationSetting: boolean;
  currentTenantName: string;
  currentUserDirectoryUuid?: string;
  onHide: () => void;
  onSuccess: () => void;
  showToast: (opts: ToastMessage) => void;
}

function buildEditUserInitialState(
  user: UserData,
  notificationSetting: boolean,
  currentTenantName: string
): EditUserState {
  return {
    directoryUuid: user.directoryUuid ?? "",
    email: user.email ?? "",
    name: {
      familyName: user.name?.familyName ?? "",
      givenName: user.name?.givenName ?? "",
    },
    attributes: {
      employeeNumber: user.attributes?.employeeNumber ?? "",
      jobRole: user.attributes?.jobRole ?? "",
      title: user.attributes?.title ?? "",
      country: user.attributes?.country ?? "",
      company: user.attributes?.company ?? "",
      businessUnit: user.attributes?.businessUnit ?? "",
      department: user.attributes?.department ?? "",
      teamName: user.attributes?.teamName ?? "",
      managersEmail: user.attributes?.managersEmail ?? "",
      managersName: user.attributes?.managersName ?? "",
      securityChampion: user.attributes?.securityChampion ?? false,
    },
    admin: mapIsAdmin(user, currentTenantName) ?? false,
    receiveActivityNotifications: notificationSetting,
  };
}

export function EditUserDialog({
  visible,
  user,
  scimEnabled,
  notificationSetting,
  currentTenantName,
  currentUserDirectoryUuid,
  onHide,
  onSuccess,
  showToast,
}: EditUserDialogProps) {
  const svcDirectoryClient = getDirectoryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [initialState, setInitialState] = useState<EditUserState>(() =>
    buildEditUserInitialState(user, notificationSetting, currentTenantName)
  );
  const [userState, setUserState] = useState<EditUserState>(initialState);
  const [emailError, setEmailError] = useState("");

  const isArchived = user.tenants?.find(t => t.tenant === currentTenantName)?.deactivated ?? false;
  const isSelf = !!user.directoryUuid && user.directoryUuid === currentUserDirectoryUuid;

  useEffect(() => {
    const s = buildEditUserInitialState(user, notificationSetting, currentTenantName);
    setInitialState(s);
    setUserState(s);
  }, [user, notificationSetting, currentTenantName]);

  const handleInputChange = (field: string, value: unknown) => {
    const path = field.split(".");
    setUserState(prev => {
      if (path.length > 1) {
        return {
          ...prev,
          [path[0]!]: { ...(prev[path[0]! as keyof EditUserState] as object), [path[1]!]: value },
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const isDuplicateEmailError = (error: unknown) =>
    (error as { message?: string })?.message?.includes("SQLSTATE 23505") &&
    (error as { message?: string })?.message?.includes("users_email_unique");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let toastState = { type: "success", message: "User updated successfully" };
    setIsLoading(true);

    const attrDiff = Object.keys(userState.attributes).reduce(
      (obj: Record<string, unknown>, key) => {
        const k = key as keyof typeof userState.attributes;
        if (userState.attributes[k] !== initialState.attributes[k])
          obj[key] = userState.attributes[k];
        return obj;
      },
      {}
    );
    const mask = Object.keys(attrDiff).join(",");
    const nameUpdated =
      userState.name.givenName !== initialState.name.givenName ||
      userState.name.familyName !== initialState.name.familyName;
    const emailUpdated = userState.email !== initialState.email;
    const attributesUpdated = Object.keys(attrDiff).length > 0;
    const roleUpdated = userState.admin !== initialState.admin;
    const notificationsUpdated =
      userState.receiveActivityNotifications !== initialState.receiveActivityNotifications;

    if (notificationsUpdated && userState.admin && user.id) {
      try {
        await updateUserNotificationsAsync(user.id, userState.receiveActivityNotifications);
      } catch {
        toastState = { type: "error", message: "Failed to update notification settings" };
      }
    }

    if (roleUpdated) {
      try {
        const res = await svcDirectoryClient.modifyUserMemberships(userState.directoryUuid, [
          {
            tenant: currentTenantName,
            ...(userState.admin
              ? { setRoles: { grantRoles: ["tenant_admin"] } }
              : { setRoles: { revokeRoles: ["tenant_admin"] } }),
          },
        ]);
        if (res.error) toastState = { type: "error", message: "Failed to update user role" };
      } catch {
        toastState = { type: "error", message: "Failed to update user role" };
      }
    }

    if (attributesUpdated || nameUpdated || emailUpdated) {
      const userToUpdate = {
        directoryUuid: userState.directoryUuid,
        tenants: [{ tenant: currentTenantName }],
        attributes: { ...attrDiff, mask },
        ...(nameUpdated
          ? { name: { givenName: userState.name.givenName, familyName: userState.name.familyName } }
          : {}),
        ...(emailUpdated ? { email: userState.email } : {}),
      };
      try {
        const res = await svcDirectoryClient.createOrUpdateUser({
          ...userToUpdate,
          attributes: userToUpdate.attributes as User["attributes"],
        });
        if (res.error) {
          if (isDuplicateEmailError(res.error)) {
            setEmailError("This email is already in use by another user.");
            setIsLoading(false);
            return;
          }
          toastState = { type: "error", message: "Failed to update user" };
        }
      } catch {
        toastState = { type: "error", message: "Failed to update user" };
      }
    }

    setIsLoading(false);
    onSuccess();

    if (toastState.type === "error") {
      showToast({ severity: "error", detail: toastState.message });
    } else {
      showToast({ severity: "success", detail: toastState.message });
      onHide();
    }
  };

  const handleArchive = async () => {
    const userToUpdate = {
      directoryUuid: user.directoryUuid,
      tenants:
        user.tenants?.map(t =>
          t.tenant === currentTenantName ? { ...t, deactivated: !isArchived } : t
        ) ?? [],
    };
    setIsLoading(true);
    try {
      const res = await svcDirectoryClient.createOrUpdateUser(userToUpdate);
      if (res.error) {
        showToast({ severity: "error", detail: "Failed to update user" });
      } else {
        showToast({ severity: "success", detail: "User updated successfully" });
        onSuccess();
      }
    } catch {
      showToast({ severity: "error", detail: "Failed to update users" });
    } finally {
      setIsLoading(false);
      if (!isArchived) onHide();
    }
  };

  const renderInputField = (field: string, value: unknown) => {
    const id = field.replace(/\./g, "-");
    if (field === "attributes.securityChampion") {
      return (
        <div key={field}>
          <FloatLabel>
            <label htmlFor={id}>Security Champion</label>
            <Dropdown
              inputId={id}
              value={value as boolean}
              options={[
                { label: "True", value: true },
                { label: "False", value: false },
              ]}
              className="w-full"
              checkmark
              highlightOnSelect={false}
              onChange={e => handleInputChange(field, e.value)}
            />
          </FloatLabel>
        </div>
      );
    }

    const label = field
      .split(".")
      .pop()!
      .replace(/([A-Z])/g, " $1")
      .replace(/family Name|given Name/, m => (m === "family Name" ? "Last Name" : "First Name"))
      .replace(/\b\w/g, c => c.toUpperCase());

    return (
      <div key={field}>
        <FloatLabel>
          <label htmlFor={id}>{label}</label>
          <InputText
            id={id}
            value={(value as string) || ""}
            className="w-full"
            autoComplete="off"
            onChange={e => {
              if (field === "email") setEmailError("");
              handleInputChange(field, e.target.value);
            }}
            {...(field === "email"
              ? {
                  "aria-required": "true",
                  "aria-invalid": !!emailError,
                  "aria-describedby": emailError ? "edit-email-error" : undefined,
                }
              : {})}
          />
        </FloatLabel>
        {field === "email" && emailError && (
          <small id="edit-email-error" role="alert" className="p-error">
            {emailError}
          </small>
        )}
      </div>
    );
  };

  const renderFields = () =>
    Object.entries(userState)
      .filter(([f]) => !["admin", "directoryUuid", "receiveActivityNotifications"].includes(f))
      .flatMap(([field, value]) =>
        typeof value === "object" && !Array.isArray(value) && value !== null
          ? Object.entries(value as Record<string, unknown>).map(([sub, subVal]) =>
              renderInputField(`${field}.${sub}`, subVal)
            )
          : renderInputField(field, value)
      );

  return (
    <Dialog
      visible={visible}
      data-testid="edit-user-dialog"
      onHide={onHide}
      header={
        scimEnabled ? (
          <div className="flex items-center w-full">
            <h2 className="text-lg font-bold mr-5">Edit User</h2>
            <Button unstyled tooltip="SCIM is currently enabled and can override edits made below.">
              <span className="p-overlay-badge body-sm">
                SCIM provisioning is enabled.
                <Badge severity="danger" style={{ minWidth: "0.5rem", height: "0.5rem" }} />
              </span>
            </Button>
          </div>
        ) : (
          "Edit User"
        )
      }
      style={{ width: "50rem" }}
      blockScroll
      maskStyle={{ backdropFilter: "blur(5px)" }}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            label={isArchived ? "Unarchive" : "Archive"}
            icon={isArchived ? "pi pi-user-plus" : "pi pi-user-minus"}
            severity={isArchived ? "success" : "danger"}
            loading={isLoading}
            onClick={() => void handleArchive()}
          />
          <Button
            label="Cancel"
            icon="pi pi-times"
            data-testid="cancel-edit-user-button"
            className="p-button-outlined p-button-danger"
            disabled={isLoading}
            onClick={onHide}
          />
          <Button
            label="Save"
            icon="pi pi-check"
            data-testid="save-edit-user-button"
            disabled={JSON.stringify(userState) === JSON.stringify(initialState)}
            onClick={e => void handleSubmit(e as unknown as React.FormEvent)}
            loading={isLoading}
          />
        </div>
      }
    >
      <form onSubmit={e => void handleSubmit(e)} className="mb-10 mt-6">
        <div className="ml-auto mb-10 flex justify-between">
          <div className="flex items-center">
            <InputSwitch
              inputId="admin-switch"
              checked={userState.admin}
              onChange={e => setUserState(prev => ({ ...prev, admin: e.value ?? false }))}
              data-testid="admin-switch"
              disabled={isSelf}
              tooltip={isSelf ? "You cannot remove your own admin rights." : undefined}
              tooltipOptions={{ showOnDisabled: true }}
            />
            <label htmlFor="admin-switch" className="ml-2 body-sm font-bold">
              Admin
            </label>
          </div>
          {userState.admin && (
            <div className="flex items-center">
              <Checkbox
                inputId="notify-checkbox"
                checked={userState.receiveActivityNotifications}
                onChange={e =>
                  setUserState(prev => ({
                    ...prev,
                    receiveActivityNotifications: e.checked ?? false,
                  }))
                }
              />
              <label htmlFor="notify-checkbox" className="ml-2 body-sm font-bold">
                Receive activity notifications
              </label>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-10">{renderFields()}</div>
      </form>
    </Dialog>
  );
}
